/**
 * Magic-Link und Passwort-Zuruecksetzen: die Sitzung aus dem URL-Fragment uebernehmen.
 *
 * **Warum das von Hand sein muss.** GoTrue `/auth/v1/verify` antwortet mit einem
 * IMPLIZITEN Fragment (`#access_token=…&refresh_token=…`). Unser Browser-Client kann
 * das nicht selbst verwerten: `createBrowserClient` aus `@supabase/ssr` setzt
 * `flowType: "pkce"` fest (`createBrowserClient.js`, nach dem Spread der eigenen
 * Optionen — also nicht ueberschreibbar), und `GoTrueClient._getSessionFromURL` wirft
 * bei genau dieser Paarung sofort:
 *
 *     case 'implicit':
 *       if (this.flowType === 'pkce') throw AuthPKCEGrantCodeExchangeError('Not a valid PKCE flow url.')
 *
 * Der Wurf passiert VOR jedem Netzwerkaufruf, und `_initialize()` verschluckt ihn
 * (nur `_debug`). Nach aussen sah das aus wie „Sitzung kam einfach nicht an":
 * gemessen am 2026-08-11 endete jeder Magic-Link auf `?error=session_failed`, ohne
 * eine einzige Anfrage an `/auth/v1/*` nach dem Redirect.
 *
 * `setSession` umgeht die Sperre, ist aber kein Trick: es prueft das Token
 * serverseitig, bevor es eine Sitzung anlegt. Ein gefaelschtes Fragment wird
 * abgewiesen. Das Fragment selbst erreicht den Server nie (Browser senden es nicht).
 *
 * **Eine bestehende Sitzung eines ANDEREN Kontos wird nicht ersetzt (ST-22).** Sonst
 * meldet ein praeparierter Link mit den Tokens eines Angreifers das Opfer suiteweit an
 * dessen Konto an (Anmelde-CSRF), und was es danach anlegt, gehoert dem Angreifer.
 * Verglichen wird `sub` aus dem Fragment-Token mit der angemeldeten Person; das Token
 * wird dafuer nur gelesen. Die Signatur prueft `setSession` (auth-js 2.104.0,
 * `_setSession`) aber NUR bei `exp` in der Zukunft (`_getUser`); ist `exp` abgelaufen oder
 * fehlt es, tauscht es ungeprueft den refresh_token. Ein unsigniertes Token mit dem `sub`
 * des Opfers und dem echten refresh_token des Angreifers bestand so den Vergleich. Neben
 * einer bestehenden Sitzung gilt deshalb nur ein Token, das noch mindestens
 * `EXP_RESERVE_S` gueltig ist — dann geht es sicher ueber `_getUser`. Den Fall OHNE
 * bestehende Sitzung schliesst das nicht — dazu braucht es eine Bestaetigung in der
 * Oberflaeche oder `token_hash` + `verifyOtp` in den Mailvorlagen.
 *
 * Der PKCE-Weg (`?code=` in der Query) bleibt unberuehrt — dafuer ist weiterhin
 * `exchangeCodeForSession` zustaendig. Diese Funktion fasst nur das Fragment an.
 */

/** Nur der Teil des Supabase-Clients, den wir hier brauchen — haelt den Test schlank. */
export interface SitzungsClient {
  auth: {
    setSession(t: { access_token: string; refresh_token: string }): Promise<{ error: { message: string } | null }>;
    /** `unknown`, damit der breitere `CallbackClient` in gui-elements hineinpasst. */
    getSession(): Promise<{ data: { session: unknown } }>;
  };
}

/** Abstand zwischen dieser Pruefung und dem `exp`-Vergleich in `_setSession` (Lock, Uhr). */
const EXP_RESERVE_S = 30;

/** `sub` und `exp` aus der Nutzlast, ungeprueft — nur zum Vergleich, nie als Anmeldung. */
function leseAnspruch(token: string): { sub: string | null; exp: number | null } {
  try {
    const teil = token.split(".")[1];
    if (!teil) return { sub: null, exp: null };
    const json = atob(teil.replace(/-/g, "+").replace(/_/g, "/"));
    const { sub, exp } = JSON.parse(json) as { sub?: unknown; exp?: unknown };
    return {
      sub: typeof sub === "string" ? sub : null,
      exp: typeof exp === "number" && Number.isFinite(exp) ? exp : null,
    };
  } catch {
    return { sub: null, exp: null };
  }
}

export interface ImpliziteSitzung {
  uebernommen: boolean;
  /** `magiclink`, `recovery`, `invite`, … — entscheidet, wohin der Aufrufer weiterleitet. */
  typ: string | null;
  fehler: string | null;
}

export async function uebernimmSitzungAusHash(
  supabase: SitzungsClient,
  hash: string,
): Promise<ImpliziteSitzung> {
  const p = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const typ = p.get("type");
  const access_token = p.get("access_token");
  const refresh_token = p.get("refresh_token");

  // Beide oder keins: mit nur einem der Tokens legt `setSession` keine Sitzung an,
  // der Versuch wuerde nur eine irrefuehrende Fehlermeldung erzeugen.
  if (!access_token || !refresh_token) return { uebernommen: false, typ, fehler: null };

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const angemeldet = (session as { user?: { id?: unknown } }).user?.id;
      const { sub, exp } = leseAnspruch(access_token);
      const nochGueltig = exp !== null && exp > Date.now() / 1000 + EXP_RESERVE_S;
      if (typeof angemeldet !== "string" || sub !== angemeldet || !nochGueltig) {
        return {
          uebernommen: false,
          typ,
          fehler: "Du bist bereits mit einem anderen Konto angemeldet. Bitte erst abmelden und den Link erneut öffnen.",
        };
      }
    }
    const { error } = await supabase.auth.setSession({ access_token, refresh_token });
    return { uebernommen: !error, typ, fehler: error?.message ?? null };
  } catch (e) {
    return { uebernommen: false, typ, fehler: e instanceof Error ? e.message : String(e) };
  }
}
